import { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { commentsAPI } from '@/api/community';
import { useToast } from '@/hooks/use-toast';
import { Send, Loader2, Bold, Italic, List, Heading2, Quote } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function CommentThread({ postId, onCommentAdded, isGuardian = false }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [newCommentContent, setNewCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  // Rich text editor for replies
  const replyEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        underline: false,
      }),
      Underline,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setReplyContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none min-h-[60px] p-2 max-w-none',
      },
    },
  });

  // Rich text editor for new comments
  const commentEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        underline: false,
      }),
      Underline,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setNewCommentContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none min-h-[80px] p-2 max-w-none',
      },
    },
  });

  useEffect(() => {
    loadComments();
  }, [postId]);

  // Reset reply editor when replyingTo changes
  useEffect(() => {
    if (replyEditor && replyingTo) {
      replyEditor.commands.clearContent();
      setReplyContent('');
    }
  }, [replyingTo, replyEditor]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await commentsAPI.getComments(postId);
      setComments(response.results || response || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load comments',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newCommentContent.trim() || isGuardian) return;

    setSubmitting(true);
    try {
      // Get HTML content from editor
      const content = commentEditor ? commentEditor.getHTML() : newCommentContent;
      await commentsAPI.createComment(postId, content);
      if (commentEditor) {
        commentEditor.commands.clearContent();
      }
      setNewCommentContent('');
      await loadComments();
      if (onCommentAdded) onCommentAdded();
      toast({
        title: 'Success',
        description: 'Comment added successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to add comment',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentCommentId) => {
    if (!replyContent.trim() || isGuardian) return;

    setSubmitting(true);
    try {
      // Get HTML content from editor
      const content = replyEditor ? replyEditor.getHTML() : replyContent;
      await commentsAPI.replyToComment(parentCommentId, content);
      if (replyEditor) {
        replyEditor.commands.clearContent();
      }
      setReplyContent('');
      setReplyingTo(null);
      await loadComments();
      if (onCommentAdded) onCommentAdded();
      toast({
        title: 'Success',
        description: 'Reply added successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to add reply',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Get profile frame URL
  const getProfileFrameUrl = (author) => {
    return author?.profile_frame_url || null;
  };

  // Get author avatar (priority: avatar > profile pic > null)
  // Frame is independent - if it exists, it will be displayed separately
  const getAuthorAvatar = (author) => {
    if (!author) return null;
    
    // Priority: avatar > profile pic
    if (author.avatar_url) {
      return author.avatar_url;
    }
    if (author.profile_pic_url) {
      return author.profile_pic_url;
    }
    return null;
  };

  const getAuthorInitials = (author) => {
    if (author?.full_name) {
      return author.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return author?.username?.slice(0, 2).toUpperCase() || 'U';
  };

  // Get formatted author name with user type
  const getAuthorDisplayName = (author) => {
    if (!author) return 'Unknown';
    const fullName = author.full_name || author.username;
    const userType = author.user_type;
    
    if (userType === 'teacher') {
      return `${fullName} (Teacher)`;
    } else if (userType === 'admin') {
      return `${fullName} (Admin)`;
    }
    return fullName;
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  const renderComment = (comment, depth = 0) => {
    const isReplying = replyingTo === comment.id;

    return (
      <div key={comment.id} className={`${depth > 0 ? 'ml-8 mt-3' : ''}`}>
        <div className="flex gap-3">
          {/* Avatar with Profile Frame */}
          <div className="relative w-8 h-8 flex-shrink-0">
            {/* Avatar/Profile Photo - Base Layer */}
            <div className="absolute inset-0 w-full h-full rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-hidden z-0" style={{ clipPath: 'circle(50% at 50% 50%)' }}>
              {getAuthorAvatar(comment.author) ? (
                <img 
                  src={getAuthorAvatar(comment.author)} 
                  alt={comment.author?.full_name || comment.author?.username}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={cn(
                "absolute inset-0 w-full h-full items-center justify-center",
                getAuthorAvatar(comment.author) ? "hidden" : "flex"
              )}>
                <span className="text-white text-xs font-semibold">{getAuthorInitials(comment.author)}</span>
              </div>
            </div>
            {/* Profile Frame Overlay - Top Layer - Larger and extending outside */}
            {getProfileFrameUrl(comment.author) && (
              <div className="absolute -inset-2 rounded-full overflow-visible pointer-events-none z-10" style={{ clipPath: 'circle(50% at 50% 50%)' }}>
                <img 
                  src={getProfileFrameUrl(comment.author)} 
                  alt="Frame"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.error('Failed to load profile frame:', getProfileFrameUrl(comment.author));
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="bg-[hsl(var(--card-bg))] rounded-lg p-3 border border-[hsl(var(--card-border))]">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm text-foreground">
                  {getAuthorDisplayName(comment.author)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(comment.created_at)}
                </span>
              </div>
              <div 
                className="text-sm text-foreground prose prose-sm dark:prose-invert max-w-none [&_a]:text-[hsl(var(--green-bright))] [&_a]:hover:text-[hsl(var(--teal-vivid))] [&_a]:underline [&_a]:underline-offset-2"
                dangerouslySetInnerHTML={{ __html: comment.content }}
              />
            </div>
            {!isGuardian && (
              <button
                onClick={() => setReplyingTo(isReplying ? null : comment.id)}
                className="mt-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {isReplying ? 'Cancel' : 'Reply'}
              </button>
            )}
            {isReplying && (
              <div className="mt-2 space-y-2">
                <Card>
                  <CardContent className="p-0">
                    {/* Toolbar */}
                    {replyEditor && (
                      <div className="border-b p-1 flex flex-wrap gap-1">
                        <button
                          onClick={() => replyEditor.chain().focus().toggleBold().run()}
                          className={cn(
                            'p-1.5 rounded transition-colors',
                            replyEditor.isActive('bold')
                              ? 'bg-[hsl(var(--green-bright))] text-white'
                              : 'hover:bg-[hsl(var(--card-border))]'
                          )}
                        >
                          <Bold className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => replyEditor.chain().focus().toggleItalic().run()}
                          className={cn(
                            'p-1.5 rounded transition-colors',
                            replyEditor.isActive('italic')
                              ? 'bg-[hsl(var(--green-bright))] text-white'
                              : 'hover:bg-[hsl(var(--card-border))]'
                          )}
                        >
                          <Italic className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => replyEditor.chain().focus().toggleHeading({ level: 2 }).run()}
                          className={cn(
                            'p-1.5 rounded transition-colors',
                            replyEditor.isActive('heading', { level: 2 })
                              ? 'bg-[hsl(var(--green-bright))] text-white'
                              : 'hover:bg-[hsl(var(--card-border))]'
                          )}
                        >
                          <Heading2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => replyEditor.chain().focus().toggleBulletList().run()}
                          className={cn(
                            'p-1.5 rounded transition-colors',
                            replyEditor.isActive('bulletList')
                              ? 'bg-[hsl(var(--green-bright))] text-white'
                              : 'hover:bg-[hsl(var(--card-border))]'
                          )}
                        >
                          <List className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => replyEditor.chain().focus().toggleBlockquote().run()}
                          className={cn(
                            'p-1.5 rounded transition-colors',
                            replyEditor.isActive('blockquote')
                              ? 'bg-[hsl(var(--green-bright))] text-white'
                              : 'hover:bg-[hsl(var(--card-border))]'
                          )}
                        >
                          <Quote className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {/* Editor */}
                    <div className="min-h-[60px]">
                      {replyEditor && <EditorContent editor={replyEditor} />}
                    </div>
                  </CardContent>
                </Card>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setReplyingTo(null);
                      if (replyEditor) {
                        replyEditor.commands.clearContent();
                      }
                      setReplyContent('');
                    }}
                    variant="outline"
                    size="sm"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleSubmitReply(comment.id)}
                    disabled={!replyContent.trim() || submitting}
                    size="sm"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-1" />
                        Reply
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-2">
            {comment.replies.map((reply) => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--green-bright))]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* New Comment Form */}
      {!isGuardian && (
        <div className="space-y-2">
          <Card>
            <CardContent className="p-0">
              {/* Toolbar */}
              {commentEditor && (
                <div className="border-b p-2 flex flex-wrap gap-1">
                  <button
                    onClick={() => commentEditor.chain().focus().toggleBold().run()}
                    className={cn(
                      'p-2 rounded transition-colors',
                      commentEditor.isActive('bold')
                        ? 'bg-[hsl(var(--green-bright))] text-white'
                        : 'hover:bg-[hsl(var(--card-border))]'
                    )}
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => commentEditor.chain().focus().toggleItalic().run()}
                    className={cn(
                      'p-2 rounded transition-colors',
                      commentEditor.isActive('italic')
                        ? 'bg-[hsl(var(--green-bright))] text-white'
                        : 'hover:bg-[hsl(var(--card-border))]'
                    )}
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => commentEditor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={cn(
                      'p-2 rounded transition-colors',
                      commentEditor.isActive('heading', { level: 2 })
                        ? 'bg-[hsl(var(--green-bright))] text-white'
                        : 'hover:bg-[hsl(var(--card-border))]'
                    )}
                  >
                    <Heading2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => commentEditor.chain().focus().toggleBulletList().run()}
                    className={cn(
                      'p-2 rounded transition-colors',
                      commentEditor.isActive('bulletList')
                        ? 'bg-[hsl(var(--green-bright))] text-white'
                        : 'hover:bg-[hsl(var(--card-border))]'
                    )}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => commentEditor.chain().focus().toggleBlockquote().run()}
                    className={cn(
                      'p-2 rounded transition-colors',
                      commentEditor.isActive('blockquote')
                        ? 'bg-[hsl(var(--green-bright))] text-white'
                        : 'hover:bg-[hsl(var(--card-border))]'
                    )}
                  >
                    <Quote className="w-4 h-4" />
                  </button>
                </div>
              )}
              {/* Editor */}
              <div className="min-h-[80px]">
                {commentEditor && <EditorContent editor={commentEditor} />}
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button
              onClick={handleSubmitComment}
              disabled={!newCommentContent.trim() || submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Comment
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((comment) => renderComment(comment))
        )}
      </div>
    </div>
  );
}
